import type { MenuItemWithDetails } from '../../lib/twin';
import { FeaturedItemCard } from './FeaturedItemCard';

interface Props {
  featured: MenuItemWithDetails[];
}

/**
 * Featured menu strip — grounded highlights pulled from the twin
 * (`is_available && is_active`, capped at 6). Doubles as the in-page `#menu`
 * target for the "Order Now" / "View full menu" CTAs, since this static demo
 * has no separate ordering app in this pass.
 */
export function FeaturedMenu({ featured }: Props) {
  return (
    <section
      id="menu"
      aria-labelledby="menu-heading"
      className="scroll-mt-4 bg-cream-100/60 py-16 sm:py-20"
    >
      <div className="mx-auto max-w-6xl px-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <h2 id="menu-heading" className="text-3xl font-bold sm:text-4xl">
              Featured today
            </h2>
            <p className="mt-3 text-lg text-espresso-soft">
              Hand-picked favorites, pulled live from today's menu. If it's not in
              stock, it's not here.
            </p>
          </div>
          <a
            href="#menu"
            className="inline-flex min-h-[44px] items-center font-semibold text-accent-dark underline-offset-4 hover:underline"
          >
            View full menu →
          </a>
        </div>

        {featured.length === 0 ? (
          <p className="mt-10 rounded-2xl bg-cream-50 p-6 text-center text-espresso-soft ring-1 ring-mocha/15">
            We're between menus right now — check back soon!
          </p>
        ) : (
          <ul className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((item) => (
              <FeaturedItemCard key={item.id} item={item} />
            ))}
          </ul>
        )}

        <p className="mt-6 text-center text-sm text-mocha">
          Full ordering &amp; checkout arrive in a later pass — this demo links to
          the in-page menu above.
        </p>
      </div>
    </section>
  );
}
